import type { AchAccountHolderType } from '@supply-mover-2-0/shared';

export interface OpaqueData {
	dataDescriptor: string;
	dataValue: string;
}

export interface AuthorizeNetCardData {
	cardNumber: string;
	month: string;
	year: string;
	cardCode?: string;
	zip?: string;
	fullName?: string;
}

export interface AuthorizeNetBankData {
	accountNumber: string;
	routingNumber: string;
	nameOnAccount: string;
	accountType: 'checking' | 'savings';
	zip?: string;
}

export interface AuthorizeNetAuthData {
	clientKey: string;
	apiLoginID: string;
}

export interface AuthorizeNetDispatchData {
	authData: AuthorizeNetAuthData;
	cardData?: AuthorizeNetCardData;
	bankData?: AuthorizeNetBankData;
}

export interface AuthorizeNetMessage {
	code: string;
	text: string;
}

export interface AuthorizeNetMessages {
	resultCode: 'Ok' | 'Error';
	message: AuthorizeNetMessage[];
}

export interface AuthorizeNetDispatchResponse {
	messages: AuthorizeNetMessages;
	opaqueData?: OpaqueData;
}

export interface PaymentGatewayConfig {
	// 'mock' (PD-15708 Q9) is the in-process test gateway; never configured
	// on a production environment.
	processorType: 'authorize_net' | 'cybersource' | 'nmi' | 'adyen' | 'mock';
	environment: 'sandbox' | 'production';
	publicCredentials: {
		// Authorize.net credentials (clientKey is shared with Adyen)
		apiLoginID?: string;
		clientKey?: string;
		// CyberSource credentials
		merchantId?: string;
		// NMI credentials
		tokenizationKey?: string;
		// Adyen credentials
		merchantAccount?: string;
		liveUrlPrefix?: string;
	};
	/**
	 * PD-15878 — the card networks this merchant account accepts, as subtype
	 * names ('Visa', 'MasterCard', …), for the card-entry form's marks.
	 *
	 * `null` means the gateway could not report its configuration, which is
	 * NOT the same as accepting nothing — the form shows no strip rather than
	 * an empty one.
	 */
	acceptedCardBrands?: Array<string> | null;
}

export interface CreatePaymentMethodRequest {
	clientId: number;

	// Tokenized data from payment processor (required for all payment methods)
	dataDescriptor: string;
	dataValue: string;

	// Payment type and subtype
	paymentType?: PaymentMethodTypeEnum;
	paymentSubtype?: PaymentMethodSubtypeEnum;

	// Billing information (not sensitive)
	paymentName?: string;
	paymentAddress1: string;
	paymentAddress2?: string;
	paymentCity: string;
	paymentState: string;
	paymentZip: string;
	paymentCountry?: string;
	paymentEmail?: string;
	paymentIsPrimary?: boolean;
	/**
	 * PD-17009 — let every account in the client's ownership group pay with
	 * this method. The server stores it only when the client belongs to an
	 * ownership group. Changing it later goes through the sharing endpoint,
	 * not the edit request.
	 */
	shareWithOwnership?: boolean;

	// Display data - only masked values, never full card/account numbers
	// Credit Card fields
	cardLastFour?: string; // Last 4 digits of card number for display
	expirationDate?: string; // Backend stores expiration date
	/**
	 * PD-15142 — how the card is funded ('credit', 'debit', 'prepaid', ...),
	 * as reported by the gateway's tokenisation step. Decides whether a
	 * processing fee may be charged: only a credit card may be surcharged.
	 * Omitted when the gateway did not report it, in which case the server
	 * resolves it before the card is first used.
	 */
	cardCategory?: string;
	/**
	 * PD-15014 — the card's BIN (first 6-8 digits) where the entry form could
	 * read it (Collect.js reports it; the Authorize.Net form holds the PAN
	 * pre-tokenisation). Keeps the stored method classifiable by the
	 * BIN-based funding lookups. Never a masked value.
	 */
	cardBin?: string;

	// eCheck fields
	routingNumber?: string; // Backend stores full routing number in paymentRoutingNumber
	accountLastFour?: string; // Last 4 digits of account number for display
	nameOnAccount?: string; // Backend stores in paymentName
	accountType?: 'checking' | 'savings'; // Backend may store in paymentSubtype or custom field
	/**
	 * PD-17299 — who holds the bank account. Decides the SEC code it is
	 * debited under (business is CCD, personal is WEB). Absent is business.
	 */
	accountHolderType?: AchAccountHolderType;
}

export interface PaymentMethodFormData {
	paymentType: PaymentMethodTypeEnum;
	// Credit card fields
	cardNumber: string;
	expirationDate: string;
	nameOnCard: string;
	securityCode: string;
	// eCheck fields
	routingNumber: string;
	accountNumber: string;
	nameOnAccount: string;
	accountType: 'checking' | 'savings';
	/** PD-17299 — business or personal; the form defaults to business. */
	accountHolderType: AchAccountHolderType;
	// Address fields
	addressLine1: string;
	addressLine2: string;
	zipCode: string;
	state: string;
	city: string;
	// Email (required for CyberSource)
	email: string;
}

export enum PaymentMethodTypeEnum {
	ACH = 'ACH',
	CREDIT_CARD = 'Credit Card',
	CREDIT_CARD_READER = 'Credit Card Reader',
	CASH = 'Cash',
	CHECK = 'Check',
	COMPANY_CREDIT = 'Company Credit',
	ACCOUNT_FUNDS = 'Account Funds',
}

export enum PaymentMethodSubtypeEnum {
	CC_AMEX = 'Amex',
	CC_DINERS_CLUB = 'Diners Club',
	CC_DISCOVER = 'Discover',
	CC_JCB = 'JCB',
	CC_MASTERCARD = 'MasterCard',
	CC_VISA = 'Visa',
	ACH_CHECKING = 'Checking',
	ACH_SAVINGS = 'Savings',
}

export enum ProcessingFeeType {
	PERCENTAGE = '%',
	FLAT_RATE = '$',
}

export class PaymentGatewayError extends Error {
	constructor(
		message: string,
		public code?: string,
		public originalError?: unknown,
	) {
		super(message);
		this.name = 'PaymentGatewayError';
	}
}
